import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { UploadSimple, FileCsv } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

export const ImportCSVDialog = ({ onImported }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) { toast.error("Choose a CSV file"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/physicians/import-csv", fd);
      toast.success(`Imported ${r.data.imported} physicians`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} rows had errors`);
      setOpen(false);
      setFile(null);
      onImported?.();
    } catch (e) {
      toast.error("Import failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="import-csv-btn" variant="outline" className="rounded-none gap-2 border-[#0A0B0F]">
          <UploadSimple size={16} weight="bold" /> Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#E2E2E5]" data-testid="import-csv-dialog">
        <DialogHeader className="border-b border-[#E2E2E5] pb-3">
          <DialogTitle className="font-display font-black tracking-tight text-2xl">Import Physicians from CSV</DialogTitle>
          <DialogDescription className="sr-only">Upload a CSV file containing physician records to bulk import them.</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="bg-[#F7F7F8] border border-[#E2E2E5] p-4 mb-4">
            <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-2">Expected Columns</div>
            <div className="font-mono text-xs text-[#0A0B0F] leading-relaxed">
              full_name (required), npi, specialty, practice_type, practice_name,<br />
              email, phone, address, city, state, zipcode, linkedin_url,<br />
              research_experience, stage
            </div>
          </div>
          <input
            ref={inputRef}
            data-testid="csv-file-input"
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
          />
          <div
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-[#E2E2E5] hover:border-[#006CFF] cursor-pointer p-8 text-center transition-colors"
          >
            <FileCsv size={36} weight="light" className="mx-auto text-[#5C5E66]" />
            <div className="mt-2 font-medium">{file ? file.name : "Click to choose CSV file"}</div>
            {file && <div className="text-xs text-[#5C5E66] mt-1 font-mono">{(file.size / 1024).toFixed(1)} KB</div>}
          </div>
        </div>
        <DialogFooter className="border-t border-[#E2E2E5] pt-3">
          <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="submit-import-btn" disabled={loading || !file} onClick={submit} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none">
            {loading ? "IMPORTING..." : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
